import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { calcWorkshopFee, formatIdr } from '../utils/workshopPricing';

const apiUrl = process.env.REACT_APP_API_ENDPOINT;

/**
 * Pilihan workshop menggantikan akomodasi hotel.
 * Simposium wajib (biaya dari symposiumFee prop).
 * Workshop opsional: tidak pilih / 1 hari / 2 hari (Sabtu + Minggu).
 */
const WorkshopSelection = ({ eventId, form, onChange, symposiumFee = 0, onWorkshopsLoaded }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [workshops, setWorkshops] = useState({ sabtu: [], minggu: [] });
  const [prices, setPrices] = useState({ one: 1199000, two: 1999000 });

  useEffect(() => {
    if (!eventId) return;

    setLoading(true);
    setError('');
    axios
      .get(`${apiUrl}/api/public/workshops`, { params: { eventId } })
      .then((res) => {
        setWorkshops({
          sabtu: res.data.sabtu || [],
          minggu: res.data.minggu || [],
        });
        const nextPrices = {
          one: res.data.priceOne ?? 1199000,
          two: res.data.priceTwo ?? 1999000,
        };
        setPrices(nextPrices);
        if (onWorkshopsLoaded) {
          onWorkshopsLoaded({
            sabtu: res.data.sabtu || [],
            minggu: res.data.minggu || [],
            prices: nextPrices,
          });
        }
      })
      .catch(() => {
        setError('Gagal memuat daftar workshop. Pastikan data workshop sudah di-setup untuk event ini.');
        setWorkshops({ sabtu: [], minggu: [] });
      })
      .finally(() => setLoading(false));
  }, [eventId]);

  const workshopFee = calcWorkshopFee(form.workshopSabtuId, form.workshopMingguId, prices);
  const symposium = Number(symposiumFee) || 0;
  const total = symposium + workshopFee;
  const wCount = (form.workshopSabtuId ? 1 : 0) + (form.workshopMingguId ? 1 : 0);

  const handleSabtuChange = (e) => {
    onChange({ target: { name: 'workshopSabtuId', value: e.target.value } });
  };

  const handleMingguChange = (e) => {
    onChange({ target: { name: 'workshopMingguId', value: e.target.value } });
  };

  if (loading) {
    return (
      <div className="mb-3 text-muted">Memuat pilihan workshop...</div>
    );
  }

  return (
    <div className="mb-3 border rounded p-3 bg-light">
      <h5 className="mb-1">Workshop (opsional)</h5>
      <p className="text-muted small mb-3">
        Simposium wajib. Workshop boleh tidak dipilih, memilih 1 workshop (Sabtu <em>atau</em> Minggu),
        atau 2 workshop di 2 hari berbeda (paket 2 hari).
      </p>

      {error && <div className="alert alert-warning py-2 small">{error}</div>}

      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label fw-semibold">Sabtu — pilih maksimal 1</label>
          <select
            className="form-select"
            name="workshopSabtuId"
            value={form.workshopSabtuId || ''}
            onChange={handleSabtuChange}
          >
            <option value="">Tidak memilih workshop Sabtu</option>
            {workshops.sabtu.map((w) => (
              <option key={w.id} value={w.id}>
                {w.kode ? `${w.kode} — ` : ''}{w.judul}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label fw-semibold">Minggu — pilih maksimal 1</label>
          <select
            className="form-select"
            name="workshopMingguId"
            value={form.workshopMingguId || ''}
            onChange={handleMingguChange}
          >
            <option value="">Tidak memilih workshop Minggu</option>
            {workshops.minggu.map((w) => (
              <option key={w.id} value={w.id}>
                {w.kode ? `${w.kode} — ` : ''}{w.judul}
              </option>
            ))}
          </select>
        </div>
      </div>

      <ul className="small text-muted mt-3 mb-2">
        <li>0 workshop: hanya biaya simposium</li>
        <li>1 workshop: {formatIdr(prices.one)}</li>
        <li>2 workshop (Sabtu + Minggu): {formatIdr(prices.two)}</li>
      </ul>

      <table className="table table-sm table-borderless mb-0">
        <tbody>
          <tr>
            <td>Biaya simposium</td>
            <td className="text-end">{formatIdr(symposium)}</td>
          </tr>
          <tr>
            <td>
              Biaya workshop
              {wCount > 0 && (
                <span className="text-muted"> ({wCount} workshop)</span>
              )}
            </td>
            <td className="text-end">{formatIdr(workshopFee)}</td>
          </tr>
          <tr className="fw-bold border-top">
            <td>Total</td>
            <td className="text-end text-primary">{formatIdr(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default WorkshopSelection;
export { calcWorkshopFee, formatIdr };
